/**
 * 수강권 카탈로그(폼 2-A) 관련 React Query 훅
 *
 * 목록 조회는 페이지가 Firestore를 직접 읽는 기존 패턴(`AdminMembersListPage`)을
 * 따르므로 본 모듈은 생성(mutation)만 노출한다.
 */

import { useMutation } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import { getFirebaseFunctions } from "@/config/firebase";

// --- 백엔드 contract (packages/@vlab-corporation/firebase-functions/src/ticket/create-ticket-catalog.ts) ---

interface CreateTicketCatalogRequest {
  centerName: string;
  title: string;
  type: "P" | "G";
  totalCoupon: number;
  validDays?: number;
  maxTrainee?: number;
}

interface CreateTicketCatalogResponse {
  success: true;
  /** UUID v4 */
  ticketId: string;
}

export interface CreateTicketCatalogParams {
  centerName: string;
  title: string;
  type: "P" | "G";
  totalCoupon: number;
  validDays?: number;
  maxTrainee?: number;
}

/**
 * 수강권 카탈로그 생성 mutation.
 *
 * 성공 시 서버에서 발급된 ticketId를 반환한다. 목록 갱신은 페이지가
 * onSuccess 콜백에서 직접 refetch한다.
 */
export function useCreateTicketCatalog() {
  return useMutation({
    mutationFn: async (params: CreateTicketCatalogParams) => {
      const fn = httpsCallable<CreateTicketCatalogRequest, CreateTicketCatalogResponse>(
        getFirebaseFunctions(),
        "createTicketCatalog",
      );
      const result = await fn(params);
      return result.data;
    },
  });
}

// --- 수정/삭제 contract (update-ticket-catalog.ts / delete-ticket-catalog.ts) ---

interface UpdateTicketCatalogRequest {
  centerId: number;
  ticketId: string;
  title?: string;
  type?: "P" | "G";
  totalCoupon?: number;
  /** number=설정, null=제거(무제한), undefined=유지 */
  validDays?: number | null;
  /** number=설정, null=제거(미지정), undefined=유지 */
  maxTrainee?: number | null;
}

interface DeleteTicketCatalogRequest {
  centerId: number;
  ticketId: string;
}

interface TicketCatalogMutationResponse {
  success: true;
}

export type UpdateTicketCatalogParams = UpdateTicketCatalogRequest;
export type DeleteTicketCatalogParams = DeleteTicketCatalogRequest;

/**
 * 수강권 카탈로그 수정 mutation. centerId+ticketId 필수, 나머지 중 1개 이상.
 * validDays/maxTrainee에 null을 주면 백엔드에서 해당 필드를 제거한다.
 */
export function useUpdateTicketCatalog() {
  return useMutation({
    mutationFn: async (params: UpdateTicketCatalogParams) => {
      const fn = httpsCallable<UpdateTicketCatalogRequest, TicketCatalogMutationResponse>(
        getFirebaseFunctions(),
        "updateTicketCatalog",
      );
      const result = await fn(params);
      return result.data;
    },
  });
}

/**
 * 수강권 카탈로그 삭제(소프트) mutation. archivedAt만 기록하며 물리 삭제하지 않는다.
 */
export function useDeleteTicketCatalog() {
  return useMutation({
    mutationFn: async (params: DeleteTicketCatalogParams) => {
      const fn = httpsCallable<DeleteTicketCatalogRequest, TicketCatalogMutationResponse>(
        getFirebaseFunctions(),
        "deleteTicketCatalog",
      );
      const result = await fn(params);
      return result.data;
    },
  });
}
